import React, { useState } from 'react';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { shadows } from '../theme/shadows';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    error?: string;
    helperText?: string;
}

export const Input: React.FC<InputProps> = ({
    label,
    error,
    helperText,
    disabled,
    style,
    onFocus,
    onBlur,
    id,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const inputId = id || props.name || label.replace(/\s+/g, '-').toLowerCase();

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[8], // Increased gap between label and input for clarity
        marginBottom: spacing[24], // Generous spacing between fields
    };

    const labelStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize, // Slightly clearer label size
        fontWeight: typography.weights.medium,
        color: colors.text.primary,
        lineHeight: typography.sizes.small.lineHeight,
    };

    const inputStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        color: colors.text.primary,
        backgroundColor: '#FFFFFF',
        // Refinement: Use Navy at low opacity for a branded but neutral border
        border: error
            ? `1px solid ${colors.status.error}`
            : isFocused
                ? `1px solid ${colors.primary.navy}`
                : `1px solid ${isHovered ? 'rgba(10, 38, 71, 0.3)' : 'rgba(10, 38, 71, 0.1)'}`,
        // Refinement: Softer corners (8px)
        borderRadius: '8px',
        // Refinement: Increased internal padding for breathable feel
        padding: `${spacing[12]} ${spacing[16]}`,
        outline: 'none',
        // Refinement: Subtle shadow on focus only, removed default shadow
        boxShadow: isFocused
            ? `0 0 0 3px ${error ? 'rgba(196, 69, 54, 0.1)' : 'rgba(10, 38, 71, 0.1)'}`
            : 'none',
        transition: 'border 150ms ease-out, box-shadow 150ms ease-out',
        width: '100%',
        boxSizing: 'border-box',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'text',
        ...style,
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        color: error ? colors.status.error : colors.text.muted,
        marginTop: spacing[4],
    };

    return (
        <div style={containerStyle}>
            <label htmlFor={inputId} style={labelStyle}>
                {label}
            </label>
            <input
                id={inputId}
                disabled={disabled}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onFocus={(e) => {
                    setIsFocused(true);
                    onFocus?.(e);
                }}
                onBlur={(e) => {
                    setIsFocused(false);
                    onBlur?.(e);
                }}
                style={inputStyle}
                {...props}
            />
            {/* Helper/Error text logic is good, keeping it simple */}
            {(error || helperText) && (
                <span style={helperStyle}>{error || helperText}</span>
            )}
        </div>
    );
};
